// AUTH-02 Two-factor challenge.
import { Navigate, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button, Form } from "react-aria-components";
import { api, call } from "../../api/client";
import { describeError } from "../../api/errors";
import { session, type AuthSession } from "../../api/session";
import { Banner } from "../../components/Banner";
import { AuthLayout } from "./AuthLayout";
import { Field } from "./fields";
import { mfaChallenge } from "./mfaChallenge";

export function TwoFactor() {
  const navigate = useNavigate();
  const challenge = mfaChallenge.get();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!challenge) return <Navigate to="/sign-in" />;

  async function submit() {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const body = useRecovery ? { mfa_token: challenge.mfa_token, recovery_code: code.trim() } : { mfa_token: challenge.mfa_token, code: code.trim() };
      const res = await call(api.POST("/auth/mfa/verify", { body }));
      mfaChallenge.clear();
      await session.start(res as AuthSession);
      void navigate({ to: challenge.redirect ?? "/" });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Two-factor authentication">
      <p>{useRecovery ? "Enter one of your recovery codes. Each one works once." : "Enter the 6-digit code from your authenticator app."}</p>
      {error && <Banner tone="critical">{error}</Banner>}
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {useRecovery ? (
          <Field label="Recovery code" name="recovery_code" autoComplete="off" isRequired value={code} onChange={setCode} autoFocus />
        ) : (
          <Field label="Code" name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} isRequired value={code} onChange={setCode} autoFocus />
        )}
        <Button type="submit" className="button primary wide" isPending={busy}>
          Verify
        </Button>
      </Form>
      <Button
        className="link-button"
        onPress={() => {
          setUseRecovery(!useRecovery);
          setCode("");
        }}
      >
        {useRecovery ? "Use your authenticator app instead" : "Use a recovery code instead"}
      </Button>
    </AuthLayout>
  );
}
